import { useState } from "react";
import { X, Scroll, Loader2, Send, Heart, Sparkles } from "lucide-react";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";

/* -----------------------------------------------------------
   SageChat — Chat avec Le Sage du Causse
   Consomme 1 carte chance, pose une énigme IA, donne un indice
   (2 lieux clignotent sur la carte) si réponse juste.
   ----------------------------------------------------------- */
export default function SageChat({
  code,
  chanceCardsUsed = 0,
  chanceCardsMax = 3,
  sageSession = null, // {question, type, accroche, isRescue} | null
  onUpdated,          // (updates) => void — parent refetches session
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState("");
  const [lastResult, setLastResult] = useState(null); // {correct, comment, gameOver, rescue}

  const cardsLeft = Math.max(0, chanceCardsMax - chanceCardsUsed);
  const hasActive = !!sageSession;

  const openModal = () => {
    setOpen(true);
    setLastResult(null);
  };
  const closeModal = () => {
    setOpen(false);
    setAnswer("");
    setLastResult(null);
  };

  const demanderEnigme = async () => {
    if (cardsLeft === 0) return;
    setLoading(true);
    try {
      const r = await gameApi.sageNouvelle(code);
      onUpdated?.(r);
      setLastResult(null);
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          "Le Sage semble absent. Réessayez dans un instant."
      );
    } finally {
      setLoading(false);
    }
  };

  const envoyerReponse = async () => {
    if (!answer.trim() || loading) return;
    setLoading(true);
    try {
      const r = await gameApi.sageRepondre(code, answer.trim());
      setLastResult(r);
      onUpdated?.(r);
      setAnswer("");
      if (r.correct && r.piste) {
        toast.success("Deux lieux dansent sur ta carte…", { duration: 3500 });
      }
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Le Sage n'a pas entendu ta réponse."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Bouton flottant — pastille en haut à droite du jeu */}
      <button
        onClick={openModal}
        data-testid="sage-toggle"
        aria-label="Consulter Le Sage du Causse"
        className="fixed top-36 sm:top-20 right-4 z-40 flex items-center gap-2 px-3 py-2 rounded-sm bg-noir-100/90 backdrop-blur border border-or/40 hover:border-or hover:bg-or/10 text-or transition-all shadow-[0_8px_24px_-6px_rgba(200,146,42,0.4)] group"
      >
        <Scroll className="w-4 h-4 group-hover:rotate-6 transition-transform" strokeWidth={1.6} />
        <span className="text-xs font-mono uppercase tracking-widest hidden sm:inline">
          Le Sage
        </span>
        <div className="flex gap-0.5 ml-1" data-testid="sage-cards">
          {Array.from({ length: chanceCardsMax }).map((_, i) => (
            <Heart
              key={i}
              className={`w-3 h-3 ${
                i < cardsLeft ? "fill-oxyde text-oxyde" : "text-parchemin/20"
              }`}
              strokeWidth={1.5}
            />
          ))}
        </div>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-noir/85 backdrop-blur-sm animate-fade-up"
          data-testid="sage-modal"
        >
          <div className="w-full max-w-lg bg-noir-100 border border-or/40 rounded-sm shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-or/20 bg-gradient-to-r from-noir to-noir-100">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-or/10 border border-or/40 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-or" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-or/70">
                    Ermite du plateau
                  </div>
                  <h3 className="font-display text-lg text-parchemin leading-tight">
                    Le Sage du Causse
                  </h3>
                </div>
              </div>
              <button
                onClick={closeModal}
                data-testid="sage-close"
                aria-label="Fermer"
                className="w-9 h-9 flex items-center justify-center border border-or/25 hover:border-or hover:bg-or/5 text-or rounded-sm transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Cartes chance visualisation */}
            <div
              className="px-5 py-3 border-b border-or/10 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-parchemin/60"
              data-testid="sage-cards-status"
            >
              <span>
                Cartes chance : {cardsLeft}/{chanceCardsMax}
              </span>
              <div className="flex gap-1">
                {Array.from({ length: chanceCardsMax }).map((_, i) => (
                  <Heart
                    key={i}
                    className={`w-3.5 h-3.5 ${
                      i < cardsLeft ? "fill-oxyde text-oxyde" : "text-parchemin/20"
                    }`}
                    strokeWidth={1.5}
                  />
                ))}
              </div>
            </div>

            {/* Corps */}
            <div className="px-5 py-6 min-h-[240px]">
              {/* Cas 1 : session Sage active (énigme en cours) */}
              {hasActive && !lastResult?.gameOver && (
                <div className="space-y-4" data-testid="sage-active">
                  {sageSession.accroche && (
                    <p className="font-display italic text-or/90 text-base leading-relaxed">
                      « {sageSession.accroche} »
                    </p>
                  )}
                  <div className="p-4 border border-or/25 bg-noir/60 rounded-sm">
                    <div className="flex items-center gap-2 mb-2 font-mono text-[10px] tracking-widest uppercase text-vert-50">
                      <Scroll className="w-3 h-3" />
                      {sageSession.isRescue
                        ? "Question de rattrapage"
                        : sageSession.type || "Énigme"}
                    </div>
                    <p
                      className="font-display text-lg text-parchemin leading-snug"
                      data-testid="sage-question"
                    >
                      {sageSession.question}
                    </p>
                  </div>

                  {/* Feedback dernière tentative */}
                  {lastResult && !lastResult.correct && lastResult.rescue && (
                    <div className="text-sm text-oxyde/90 bg-oxyde/10 border border-oxyde/30 px-3 py-2 rounded-sm">
                      Pas cette fois — Le Sage te tend une question plus simple.
                    </div>
                  )}

                  {/* Input */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      autoFocus
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && envoyerReponse()}
                      placeholder="Ta réponse…"
                      data-testid="sage-input"
                      className="flex-1 bg-noir border border-or/30 focus:border-or px-3 py-2.5 text-parchemin placeholder:text-parchemin/30 outline-none font-mono text-sm rounded-sm"
                    />
                    <button
                      onClick={envoyerReponse}
                      disabled={loading || !answer.trim()}
                      data-testid="sage-submit"
                      className="px-4 py-2.5 bg-or hover:bg-or/90 disabled:opacity-40 disabled:cursor-not-allowed text-noir font-mono text-xs uppercase tracking-widest rounded-sm flex items-center gap-1.5 transition"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Cas 2 : résultat juste ou faux final */}
              {lastResult && lastResult.correct && (
                <div className="text-center py-4" data-testid="sage-result-juste">
                  <Sparkles className="w-10 h-10 text-or mx-auto mb-3 animate-pulse" />
                  <p className="font-display italic text-parchemin text-lg leading-relaxed mb-3">
                    « {lastResult.comment} »
                  </p>
                  <p className="text-xs font-mono uppercase tracking-widest text-vert-50">
                    Ouvre ta carte pour voir les 2 lieux clignoter
                  </p>
                </div>
              )}
              {lastResult && !lastResult.correct && lastResult.gameOver && (
                <div className="text-center py-4" data-testid="sage-result-faux">
                  <div className="w-10 h-10 mx-auto mb-3 opacity-50 text-oxyde">
                    <Scroll className="w-full h-full" strokeWidth={1.4} />
                  </div>
                  <p className="font-display italic text-parchemin/80 text-lg leading-relaxed">
                    « {lastResult.comment} »
                  </p>
                </div>
              )}

              {/* Cas 3 : pas de session active — proposer d'en démarrer une */}
              {!hasActive && !lastResult && (
                <div className="text-center py-2" data-testid="sage-idle">
                  <p className="font-display text-parchemin text-base leading-relaxed mb-4">
                    Le Sage veille sur le plateau. En échange d&apos;une carte chance,
                    il te posera une énigme. Réponds juste et deux lieux
                    apparaîtront dans le brouillard de ta carte — l&apos;un d&apos;eux est
                    ton prochain point.
                  </p>
                  {cardsLeft === 0 ? (
                    <p className="text-oxyde/80 font-mono text-xs uppercase tracking-widest">
                      Toutes vos cartes chance sont épuisées
                    </p>
                  ) : (
                    <button
                      onClick={demanderEnigme}
                      disabled={loading}
                      data-testid="sage-request-btn"
                      className="mt-2 px-5 py-3 bg-or hover:bg-or/90 disabled:opacity-40 text-noir font-mono text-xs uppercase tracking-widest rounded-sm transition inline-flex items-center gap-2"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4" />
                      )}
                      Consulter le Sage · –1 carte
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
